import React, { useEffect, useState } from 'react';

import { useCustomAlerts } from '@/shared/lib/alert';

import { IBookmarkFolderItemState } from '@/shared/types/bookmarkFolder';
import { TMediaTab } from '@/shared/types/media';

import { DIRECTOR_ROUTES } from '@/shared/constants';

import { useBookmarkFavoriteUserMutation } from '@/entities/user';

import { IUserCardBase } from './CardTypes';

interface IUserCardActionsCallbacks<T extends IUserCardBase> {
  /** Вызывается после успешного добавления в избранное, с id актёра */
  onBookmarked?: (targetId: number) => void;
  /**
   * Вызывается после успешного снятия с избранного, с id актёра, ПРЕЖНИМИ
   * (уже обнулёнными локально) `folderIds` — экран считает по ним снекбар с
   * отменой — и `restore`, который возвращает карточку в прежнее состояние.
   *
   * `restore` нужен потому, что «Вернуть» нажимают на СНЕКБАРЕ, а состояние
   * закладки живёт в карточке: список `/actors` приходит POST-мутацией
   * (`useFindUsers`), инвалидировать его нечем, и без возврата карточка
   * оставалась бы «не в избранном», хотя сервер уже восстановил и закладку, и
   * папки. На экранах со списком-запросом вызов безвреден: карточку к этому
   * моменту уже размонтировал рефетч, и setState на ней — no-op
   */
  onUnbookmarked?: (
    targetId: number,
    removedFolderIds: number[],
    restore: () => void
  ) => void;
  /**
   * Открыть окно быстрого просмотра вместо перехода на страницу актёра (18968).
   *
   * Экраны со списком актёров передают его, остальные (хаб откликов,
   * приглашения) — нет, и там клик по карточке по-прежнему уводит на анкету.
   * Развилка живёт здесь, а не в каждом виде карточки: иначе три копии разошлись
   * бы ровно так же, как разошлась бы проверка закрытого профиля рядом
   */
  onQuickView?: (user: T, tab?: TMediaTab) => void;
}

/**
 * Поведение карточки актёра, одинаковое во всех её видах: куда ведёт клик, что
 * происходит с закрытым профилем и как переключается избранное.
 *
 * Вынесено из `Card.tsx` при появлении видов `list`/`compact`/`photos`: три копии
 * этой логики разошлись бы, и закрытый профиль в одном из видов рано или поздно
 * стал бы кликабельным.
 * @param user - данные актёра из списочного ответа
 * @param link - ссылка перехода, если экрану нужна не карточка профиля
 * @param callbacks - необязательные колбэки экрана для снекбаров папок
 * избранного (`useFavoriteFolderSnackbars`); карточка/хук про снекбары не знают
 */
export const useUserCardActions = <T extends IUserCardBase>(
  user: T,
  link?: string,
  callbacks?: IUserCardActionsCallbacks<T>
) => {
  const [isBookmarked, setIsBookmarked] = useState<boolean>(user.bookmarked);
  const [folderIds, setFolderIds] = useState<number[]>(user.folder_ids ?? []);

  const { mutate: mutateBookmarkUser, isPending: isLoadingBookmarkUser } =
    useBookmarkFavoriteUserMutation();

  const { showCustomAlert } = useCustomAlerts();

  const actorLink = link
    ? link
    : DIRECTOR_ROUTES.actors.show.route(Number(user.id));

  /**
   * Событие клика по карточке(если профиль закрыт отменяем редирект и показываем подсказку)
   * @param e - событие браузера
   */
  const handleCardClick = (e: React.MouseEvent) => {
    if (!user.visible) {
      e.preventDefault();
      showCustomAlert('Профиль актёра закрыт', { severity: 'info' });
      return;
    }

    // Порядок важен: закрытый профиль окно тоже не открывает — там нечего
    // показывать, и подсказка про закрытый профиль остаётся единственной реакцией
    if (callbacks?.onQuickView) {
      e.preventDefault();
      callbacks.onQuickView(user);
    }
  };

  /**
   * Переключение избранного. Клик не должен всплыть до ссылки карточки, иначе
   * добавление в избранное уводило бы на профиль актёра.
   *
   * Снятие сердечка при непустых `folderIds` сбрасывает их в `[]`: сервер
   * каскадом удаляет привязки элемента к папкам вместе с общим избранным, и
   * клиентское состояние обязано отразить это сразу, не дожидаясь рефетча.
   * @param e - событие браузера
   */
  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const wasBookmarked = isBookmarked;

    mutateBookmarkUser(
      {
        id: Number(user.id),
        action: wasBookmarked ? 'remove' : 'add',
        // При снятии — папки, где актёр состоял: мутация по ним синхронно
        // правит счётчики в блоке «Мои папки»
        folderIds: wasBookmarked ? folderIds : undefined
      },
      {
        /**
         * Выполнится после успешной мутации
         */
        onSuccess: () => {
          setIsBookmarked(!wasBookmarked);

          if (wasBookmarked) {
            if (folderIds.length > 0) {
              setFolderIds([]);
            }
            callbacks?.onUnbookmarked?.(Number(user.id), folderIds, () => {
              setIsBookmarked(true);
              setFolderIds(folderIds);
            });
          } else {
            callbacks?.onBookmarked?.(Number(user.id));
          }
        }
      }
    );
  };

  /**
   * Состояние после привязки/снятия папки: первая галка на элементе вне
   * избранного добавляет и в избранное — сердечко закрашивается в тот же момент
   * @param state - ответ сервера
   */
  const handleFolderIdsChange = (state: IBookmarkFolderItemState) => {
    setFolderIds(state.folder_ids);
    setIsBookmarked(state.bookmarked);
  };

  // Обновляем состояние избранного при изменении данных пользователя
  useEffect(() => {
    setIsBookmarked(user.bookmarked);
  }, [user.id, user.bookmarked]);

  useEffect(() => {
    setFolderIds(user.folder_ids ?? []);
  }, [user.id, user.folder_ids]);

  return {
    actorLink,
    folderIds,
    handleBookmarkClick,
    handleCardClick,
    handleFolderIdsChange,
    isBookmarked,
    isLoadingBookmarkUser
  };
};
